/**
 * resourceCounters — pure helpers for the Check-In custom resource-counter
 * table (Phase 2.1 Task:1, restored). Kept framework-free so the diff/new-value
 * math and the Hobbs cascade are unit-testable without a React harness.
 *
 * Counter model (mirrors legacy `calendar.event-checkin.js:146-205`):
 *   - `counterValue`    LIVE base reading from check-in-start (NOT a snapshot).
 *   - `diffCounter`     user-entered delta (string in state for the input).
 *   - `newCounterValue` = `counterValue + diffCounter`, 2 decimals.
 *
 * @module resourceCounters
 */

/**
 * Compute `newCounterValue = counterValue + diffCounter` to 2 decimals.
 * Returns the supplied `fallback` when either input is not finite (so a
 * mid-typing partial value like "" or "-" doesn't blank a prior good value).
 *
 * @param {number|string} counterValue base reading
 * @param {number|string} diffCounter  user delta
 * @param {string} [fallback='']       value to keep when inputs aren't finite
 * @returns {string}
 */
export function computeNewCounterValue(counterValue, diffCounter, fallback = '') {
	// `Number('')` is 0 (not NaN), so guard empty/whitespace strings explicitly —
	// a cleared input must keep the fallback, not compute base + 0.
	const diffRaw = typeof diffCounter === 'string' ? diffCounter.trim() : diffCounter;
	if (diffRaw === '' || diffRaw == null) return fallback;
	const base = Number(counterValue);
	const diff = Number(diffRaw);
	if (!Number.isFinite(base) || !Number.isFinite(diff)) return fallback;
	return (base + diff).toFixed(2);
}

/**
 * Whether a resource's configured meter is Hobbs, per the legacy rule: an
 * instructor resource follows the org `instructorMeter`, every other resource
 * follows the org `resourceMeter`.
 *
 * @param {string} type             resource system-unique type (e.g. 'instructor')
 * @param {string} instructorMeter  org setting
 * @param {string} resourceMeter    org setting
 * @returns {boolean}
 */
export function resourceUsesHobbs(type, instructorMeter, resourceMeter) {
	return type === 'instructor' ? instructorMeter === 'hobbs' : resourceMeter === 'hobbs';
}

/**
 * Cascade a Hobbs meter End change into the custom resource counters. For each
 * hobbs-sourced resource, set `diffCounter = HobbsEnd − HobbsStart` and
 * `newCounterValue = counterValue + diff` (2 dp). No-op (returns the SAME map
 * reference) when the Hobbs reading is incomplete or went backwards
 * (`HobbsEnd < HobbsStart`) — matching legacy "only when value >= hobbsValue".
 *
 * Pure: never mutates the input; returns a new map only when something changed.
 *
 * @param {Object<string,{type:string,counterValue:number|string,diffCounter:string,newCounterValue:string}>} resourceCounters
 * @param {Object} args
 * @param {number|string} args.hobbsStart        meter LIVE start value
 * @param {number|string} args.hobbsEnd          entered End value
 * @param {string} args.instructorMeter          org setting
 * @param {string} args.resourceMeter            org setting
 * @returns {Object} updated (or original) resourceCounters map
 */
export function cascadeHobbsToResources(resourceCounters, args) {
	const { hobbsStart, hobbsEnd, instructorMeter, resourceMeter } = args || {};
	const start = Number(hobbsStart);
	const end = Number(hobbsEnd);
	if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return resourceCounters;
	const diff = end - start;
	const diffText = diff.toFixed(2);
	let changed = false;
	const next = { ...resourceCounters };
	Object.keys(resourceCounters).forEach(id => {
		const r = resourceCounters[id];
		if (!r || !resourceUsesHobbs(r.type, instructorMeter, resourceMeter)) return;
		const base = Number(r.counterValue);
		if (!Number.isFinite(base)) return;
		next[id] = { ...r, diffCounter: diffText, newCounterValue: (base + diff).toFixed(2) };
		changed = true;
	});
	return changed ? next : resourceCounters;
}
